import React, { useEffect, useState, useRef } from 'react';
import { format } from 'date-fns';
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { LoadingSpinner } from "../ui/loading-spinner";
import { useInvoice } from "../../pages/InvoiceApp";
import { cn } from "../../lib/utils";
import { useTheme } from '../ui/theme-provider'; // Import useTheme

// Add memo to prevent unnecessary re-renders
// Accept a prop to control whether to show the preview button
export const InvoiceSummary = React.memo(({ hidePreviewButton = false }) => {
  const {
    total,
    summary,
    rateType,
    previewLoading,
    handleGeneratePreview,
    formatCurrency,
    entries,
    setSummary
  } = useInvoice();
  const { theme } = useTheme(); // Get theme
  
  // Use a ref to track if summary was manually calculated
  const wasManuallyCalculated = useRef(false);
  
  // Keep track of entries that have been processed 
  const [processedEntryIds, setProcessedEntryIds] = useState([]);

  // Calculate entry total - implement locally since it's not provided
  const calculateEntryTotal = (entry) => {
    if (!entry || !entry.patients) return 0;
    
    return entry.patients.reduce((sum, patient) => {
      const count = parseFloat(patient.count) || 0;
      const rate = parseFloat(patient.rate) || 0;
      return sum + (count * rate);
    }, 0);
  };
  
  // Move summary calculation to useEffect to avoid state updates during render
  useEffect(() => {
    // Skip this effect if we already have a manually calculated summary
    // This prevents overriding the detailed descriptions from calculateTotal
    if (wasManuallyCalculated.current && summary.length > 0) {
      return;
    }
    
    console.log('Current entries in summary effect:', entries);
    
    // Skip if entries is empty or undefined
    if (!entries || entries.length === 0) return;
    
    // Don't automatically generate summaries - let the user click "Calculate Total" instead
    // This fixes the issue where descriptions get reset
  }, [entries, setSummary, summary.length]);
  
  // Add an effect to detect when the "Calculate Total" button has been clicked
  useEffect(() => {
    if (summary.length > 0) {
      wasManuallyCalculated.current = true;
      
      // Generate an ID list of the current entries to track changes
      const entryIds = entries
        .filter(entry => entry.date)
        .map(entry => entry.date.toString() + (entry.description || ''));
      
      setProcessedEntryIds(entryIds);
    }
  }, [summary, entries]);
  
  // Check if all needed data is valid before allowing preview generation
  const canGeneratePreview = () => {
    // Make sure we have total calculated
    if (!total) return false;
    
    // Make sure we have at least one entry with a date
    const hasValidEntries = entries?.some(entry => entry.date);
    if (!hasValidEntries) return false;
    
    return true;
  };
  
  // Log when generate preview is clicked
  const handlePreviewClick = () => {
    console.log('Generate preview button clicked!');
    console.log('Current entries:', entries);
    console.log('Total:', total);
    handleGeneratePreview();
  };
  
  // Skip rendering if there's nothing to show yet
  if (!total) {
    return null;
  }
  
  return (
    // Card: Theme-aware
    <Card className={cn(
      "shadow-lg",
      theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3F] border-purple-500/20'
    )}>
      <CardContent className="p-4">
        {/* Title: Theme-aware */}
        <h2 className={cn(
          "text-lg font-medium mb-4",
          theme === 'light' ? 'text-black' : 'text-white'
        )}>Invoice Summary</h2>
        
        {/* Inner Summary Box: Theme-aware */}
        <div className={cn(
          "p-4 rounded-lg mb-4",
          theme === 'light' ? 'bg-gray-100' : 'bg-[#1F1F2E]'
        )}>
          <div className="flex items-center justify-between">
            {/* Show patients count for percentage and per_patient rate types */}
            {(rateType === 'percentage' || rateType === 'per_patient') && (
              <div className="flex-1">
                {/* Label: Theme-aware */}
                <p className={cn("text-sm", theme === 'light' ? 'text-gray-600' : 'text-gray-400')}>Total Patients</p>
                {/* Value: Theme-aware */}
                <p className={cn("text-lg font-medium", theme === 'light' ? 'text-black' : 'text-white')}>{total?.patients || 0}</p>
              </div>
            )}
            
            {/* Show hours only for hourly and fixed rate types */}
            {(rateType === 'hourly' || rateType === 'fixed') && (
              <div className="flex-1">
                {/* Label: Theme-aware */}
                <p className={cn("text-sm", theme === 'light' ? 'text-gray-600' : 'text-gray-400')}>Total Hours</p>
                {/* Value: Theme-aware */}
                <p className={cn("text-lg font-medium", theme === 'light' ? 'text-black' : 'text-white')}>{total?.hours || 0}</p>
              </div>
            )}
            
            {/* Always show amount */}
            <div className="flex-1 text-right">
              {/* Label: Theme-aware */}
              <p className={cn("text-sm", theme === 'light' ? 'text-gray-600' : 'text-gray-400')}>Total Amount</p>
              {/* Value: Theme-aware */}
              <p className={cn("text-lg font-medium", theme === 'light' ? 'text-black' : 'text-white')}>{formatCurrency(total?.amount || 0)}</p>
            </div>
          </div>
        </div>
        
        {summary.length > 0 && (
          // Detailed Summary Box: Theme-aware
          <div className={cn(
            "p-4 rounded-lg",
            theme === 'light' ? 'bg-gray-100' : 'bg-[#1F1F2E]'
          )}>
            {/* Title: Theme-aware */}
            <h3 className={cn(
              "text-sm font-medium mb-2",
              theme === 'light' ? 'text-purple-700' : 'text-purple-300'
            )}>Detailed Summary</h3>
            <ul className="space-y-1">
              {summary.map((item, index) => {
                // Check if this is a special row
                const isSpecialRow = item.isSubtotal || item.isPercentage || item.isTotal;
                
                // Parse the value to display it better if it contains an equals sign
                const hasValue = item.value && item.value.includes('=');
                const parts = hasValue ? item.value.split('=') : [item.value, ''];
                
                return (
                  // List Item: Theme-aware text with special styling for summary rows
                  <li 
                    key={index} 
                    className={cn(
                      "text-sm",
                      isSpecialRow ? "font-semibold border-t pt-2 mt-2" : "",
                      isSpecialRow 
                        ? (theme === 'light' ? 'text-gray-900 border-purple-600' : 'text-white border-purple-500')
                        : (theme === 'light' ? 'text-gray-700' : 'text-gray-300')
                    )}
                  >
                    {/* Label part: Theme-aware */}
                    <span className={cn(
                      isSpecialRow
                        ? (theme === 'light' ? 'text-purple-700' : 'text-purple-300')
                        : (theme === 'light' ? 'text-gray-500' : 'text-gray-400')
                    )}>{item.label}:</span>{' '}
                    {/* Value part: Theme-aware */}
                    <span>{parts[0].trim()}</span>
                    {/* Equals part: Theme-aware */}
                    {hasValue && <span className={cn(theme === 'light' ? 'text-gray-500' : 'text-gray-400')}> = {parts[1].trim()}</span>}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        
        {/* Only show the preview button if not hidden by prop */}
        {!hidePreviewButton && (
          <div className="mt-4 flex justify-end">
            {/* Button: Consistent purple */}
            <Button 
              onClick={handlePreviewClick}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6"
              disabled={!canGeneratePreview() || previewLoading}
            >
              {previewLoading ? <LoadingSpinner size="small" /> : "Generate Invoice Preview"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
});
